import { useSnackbar } from "notistack";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getStoredRole, isUserAdminRole, ROLE_ADMIN } from "../../../constants/roles";
import { useIsSuperAdmin } from "../../../hooks/useIsSuperAdmin";
import {
  Box,
  Typography,
  Stack,
  InputBase,
  FormControl,
  Select,
  MenuItem,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

import {
  DeleteUser,
  GetUserById,
  UpdateUser,
} from "../../../hooks/useUserData";
import { GetUserGroupsByOrganisationId } from "../../../hooks/useUserGroupdata";
import { GetFacilitiesByOrganisationId } from "../../../hooks/useFacilityData";

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";

const AVATAR_GRADIENTS = [
  ["#8a1125", "#c23b54"],
  ["#10324a", "#2d5a7e"],
  ["#3d2645", "#6b4a7d"],
  ["#4a3218", "#8a5a2b"],
  ["#1f3d2e", "#3d6b52"],
  ["#2c1810", "#5c3820"],
  ["#0a1628", "#2d3e5a"],
];

const hashString = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const gradientFor = (name) =>
  AVATAR_GRADIENTS[hashString(name) % AVATAR_GRADIENTS.length];

const initialsFor = (first = "", last = "") => {
  const f = (first || "").trim();
  const l = (last || "").trim();
  if (!f && !l) return "··";
  if (f && l) return (f[0] + l[0]).toUpperCase();
  return (f || l).slice(0, 2).toUpperCase();
};

const Avatar = ({ first, last, size = 64 }) => {
  const seed = `${first || ""} ${last || ""}`.trim() || "?";
  const [a, b] = gradientFor(seed);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "16px",
        background: `linear-gradient(135deg, ${a} 0%, ${b} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        boxShadow: `0 1px 0 rgba(255,255,255,0.4) inset, 0 14px 30px -14px ${a}66`,
        transition: "background 0.4s ease",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "16px",
          background:
            "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.24), transparent 60%)",
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: size * 0.36,
          letterSpacing: "-0.02em",
          color: "#fff",
          position: "relative",
          zIndex: 1,
        }}
      >
        {initialsFor(first, last)}
      </Typography>
    </Box>
  );
};

const SectionLabel = ({ index, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "11px",
        fontWeight: 600,
        color: BRAND,
        letterSpacing: "0.1em",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {index}
    </Typography>
    <Box sx={{ width: 14, height: "1px", background: LINE }} />
    <Typography
      sx={{
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.24em",
        textTransform: "uppercase",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const FieldLabel = ({ children, required }) => (
  <Typography
    sx={{
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: INK_MUTED,
      mb: 1,
      fontFamily: '"DM Sans", sans-serif',
    }}
  >
    {children}
    {required && (
      <Box component="span" sx={{ color: BRAND, ml: 0.5 }}>
        *
      </Box>
    )}
  </Typography>
);

const TextInput = ({ value, onChange, placeholder, type = "text" }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 2,
      height: 48,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "12px",
      transition: "all 0.25s ease",
      "&:hover": { borderColor: "rgba(10,22,40,0.2)" },
      "&:focus-within": {
        borderColor: "rgba(10,22,40,0.32)",
        boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
      },
    }}
  >
    <InputBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      fullWidth
      sx={{
        fontSize: "15px",
        fontWeight: 500,
        fontFamily: '"DM Sans", sans-serif',
        color: INK,
        "& input::placeholder": { color: INK_MUTED, opacity: 1 },
      }}
    />
  </Box>
);

const menuPaperProps = {
  PaperProps: {
    sx: {
      mt: 1,
      borderRadius: "12px",
      border: `1px solid ${LINE}`,
      boxShadow: "0 24px 48px -24px rgba(10,22,40,0.18)",
      "& .MuiMenuItem-root": {
        fontSize: "14px",
        fontFamily: '"DM Sans", sans-serif',
        py: 1.25,
        px: 2,
        "&.Mui-selected": {
          background: "rgba(10,22,40,0.04)",
          "&:hover": { background: "rgba(10,22,40,0.06)" },
        },
      },
    },
  },
};

const selectSx = {
  height: 48,
  background: "#fff",
  fontSize: "15px",
  fontWeight: 500,
  fontFamily: '"DM Sans", sans-serif',
  color: INK,
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: LINE,
    borderRadius: "12px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.2)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.32)",
    borderWidth: "1px",
  },
  "& .MuiSelect-icon": { color: INK_MUTED },
};

export const UserEditModal = ({ userId, onClose, showRoleSelect = false }) => {
  // A user_admin may edit users but never change roles — hide the picker for
  // them (the backend strips role changes from user_admin requests regardless).
  // Otherwise the assignable options follow the ladder: superadmin → admin,
  // admin → user_admin, both → user.
  const { isSuperAdmin } = useIsSuperAdmin();
  const editorRole = getStoredRole();
  const canSelectRole = showRoleSelect && !isUserAdminRole(editorRole);
  const canAssignAdmin = isSuperAdmin;
  const canAssignUserAdmin = isSuperAdmin || editorRole === ROLE_ADMIN;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [edited, setEdited] = useState(false);
  const [firstName, setFirstname] = useState("");
  const [lastName, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [group, setGroup] = useState("");
  const [facility, setFacility] = useState("");
  const [role, setRole] = useState("user");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentOrganisationId, setCurrentOrganisationId] = useState(null);
  // Tracks which user the form has been populated from. Lets the init effect
  // run once per user instead of on every react-query refetch — otherwise a
  // background refetch would overwrite the edits in progress.
  const initialisedForRef = useRef(null);

  const { data: user, isLoading } = GetUserById(userId);
  const { data: userGroupData } = GetUserGroupsByOrganisationId(
    user?.data?.organisation_id
  );
  const { data: facilitiesData } = GetFacilitiesByOrganisationId(
    user?.data?.organisation_id || currentOrganisationId
  );

  useEffect(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const { organisation_id } = JSON.parse(userData);
        setCurrentOrganisationId(organisation_id);
      }
    } catch (e) {
      console.error("Error getting organisation id:", e);
    }
  }, []);

  useEffect(() => {
    if (user?.data && initialisedForRef.current !== userId) {
      initialisedForRef.current = userId;
      setFirstname(user.data.first_name || "");
      setLastname(user.data.last_name || "");
      setEmail(user.data.email || "");
      setPhone(user.data.phone || "");
      setRole(user.data.role || "user");

      if (user.data.group) {
        const gid =
          typeof user.data.group.id === "string"
            ? parseInt(user.data.group.id, 10)
            : user.data.group.id;
        setGroup(gid);
      } else if (user.data.group_id) {
        const gid =
          typeof user.data.group_id === "string"
            ? parseInt(user.data.group_id, 10)
            : user.data.group_id;
        setGroup(gid);
      } else {
        setGroup("");
      }

      if (user.data.facility) {
        const fid =
          typeof user.data.facility.id === "string"
            ? parseInt(user.data.facility.id, 10)
            : user.data.facility.id;
        setFacility(fid);
      } else if (user.data.facility_id) {
        const fid =
          typeof user.data.facility_id === "string"
            ? parseInt(user.data.facility_id, 10)
            : user.data.facility_id;
        setFacility(fid);
      } else {
        setFacility("");
      }
    }
  }, [user, userId]);

  useEffect(() => {
    if (!confirmDelete) return undefined;
    const t = setTimeout(() => setConfirmDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  const groupOptions = React.useMemo(() => {
    const base = (userGroupData?.data || []).map((g) => ({
      id: g.id,
      label: g.group_name,
    }));
    if (user?.data?.group) {
      const curr = {
        id: user.data.group.id,
        label: user.data.group.group_name,
      };
      if (!base.some((g) => g.id === curr.id)) base.unshift(curr);
    }
    return base;
  }, [userGroupData, user]);

  const facilityOptions = React.useMemo(() => {
    const base = (facilitiesData?.data || []).map((f) => ({
      id: f.id,
      label: f.facility_name,
    }));
    if (user?.data?.facility) {
      const curr = {
        id: user.data.facility.id,
        label: user.data.facility.facility_name,
      };
      if (!base.some((f) => f.id === curr.id)) base.unshift(curr);
    }
    return base;
  }, [facilitiesData, user]);

  const deleteUserMutation = DeleteUser({
    onSuccess: () => {
      enqueueSnackbar("Användare raderad.", { variant: "success" });
      onClose && onClose();
    },
    onError: () => {
      enqueueSnackbar("Kunde inte radera användare.", { variant: "error" });
      setConfirmDelete(false);
    },
  });

  const updateUserMutation = UpdateUser({
    onSuccess: () => {
      enqueueSnackbar("Användare uppdaterad.", { variant: "success" });
      onClose && onClose();
    },
    onError: () => {
      enqueueSnackbar("Kunde inte uppdatera användare.", { variant: "error" });
    },
  });

  const handleDeleteClick = () => {
    if (confirmDelete) {
      deleteUserMutation.mutate(userId);
    } else {
      setConfirmDelete(true);
    }
  };

  const handleUpdate = () => {
    // Phone is intentionally optional here — older users may lack one, and
    // that should not block editing their role/group.
    if (!firstName.trim()) {
      enqueueSnackbar("Förnamn saknas.", { variant: "error" });
      return;
    }
    if (!lastName.trim()) {
      enqueueSnackbar("Efternamn saknas.", { variant: "error" });
      return;
    }
    if (!email.trim()) {
      enqueueSnackbar("E-postadress saknas.", { variant: "error" });
      return;
    }
    if (!group) {
      enqueueSnackbar("Grupp måste väljas.", { variant: "error" });
      return;
    }
    updateUserMutation.mutate({
      userId,
      userData: {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        group_id: group,
        facility_id: facility || null,
        ...(canSelectRole ? { role } : {}),
      },
    });
  };

  const saving = updateUserMutation.isLoading;
  const deleting = deleteUserMutation.isLoading;
  // The button only gates on "has something changed" — required-field checks
  // run in handleUpdate so the user gets a message saying what is missing,
  // instead of a silently disabled button.
  const canSave = edited && !saving;

  const markEdited = (setter) => (v) => {
    setter(v);
    setEdited(true);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2.5,
          mb: 4,
          pb: 3,
          borderBottom: `1px solid ${LINE_SOFT}`,
        }}
      >
        <Avatar first={firstName} last={lastName} size={64} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "10.5px",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
              mb: 0.5,
            }}
          >
            Användare
          </Typography>
          {isLoading && !firstName ? (
            <Skeleton width="65%" height={30} />
          ) : (
            <Typography
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontSize: "1.5rem",
                fontWeight: 600,
                color: INK,
                letterSpacing: "-0.025em",
                lineHeight: 1.15,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {[firstName, lastName].filter(Boolean).join(" ") ||
                "Namnlös användare"}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionLabel index="01">Personuppgifter</SectionLabel>
        <Stack spacing={2.25}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <FieldLabel required>Förnamn</FieldLabel>
              <TextInput
                value={firstName}
                onChange={(e) => markEdited(setFirstname)(e.target.value)}
                placeholder="Förnamn"
              />
            </Box>
            <Box>
              <FieldLabel required>Efternamn</FieldLabel>
              <TextInput
                value={lastName}
                onChange={(e) => markEdited(setLastname)(e.target.value)}
                placeholder="Efternamn"
              />
            </Box>
          </Box>
          <Box>
            <FieldLabel required>E-postadress</FieldLabel>
            <TextInput
              value={email}
              onChange={(e) => markEdited(setEmail)(e.target.value)}
              placeholder="namn@företag.se"
              type="email"
            />
          </Box>
          <Box>
            <FieldLabel>Telefonnummer</FieldLabel>
            <TextInput
              value={phone}
              onChange={(e) => markEdited(setPhone)(e.target.value)}
              placeholder="0701234567"
              type="tel"
            />
          </Box>
        </Stack>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionLabel index="02">Tillhörighet</SectionLabel>
        <Stack spacing={2.25}>
          {canSelectRole && (
            <Box>
              <FieldLabel required>Roll</FieldLabel>
              <FormControl fullWidth>
                <Select
                  value={role}
                  onChange={(e) => markEdited(setRole)(e.target.value)}
                  IconComponent={KeyboardArrowDownRoundedIcon}
                  MenuProps={menuPaperProps}
                  sx={selectSx}
                >
                  <MenuItem value="user">Vanlig användare</MenuItem>
                  {canAssignUserAdmin && (
                    <MenuItem value="user_admin">Användaradmin</MenuItem>
                  )}
                  {canAssignAdmin && <MenuItem value="admin">Admin</MenuItem>}
                </Select>
              </FormControl>
              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: "12px",
                  color: INK_MUTED,
                  fontFamily: '"DM Sans", sans-serif',
                }}
              >
                {role === "admin"
                  ? "Kan logga in i webbverktyget och hantera organisationen."
                  : "Endast mobilappen — ingen åtkomst till webbverktyget."}
              </Typography>
            </Box>
          )}
          <Box>
            <FieldLabel required>Grupp</FieldLabel>
            <FormControl fullWidth>
              <Select
                value={group}
                onChange={(e) => markEdited(setGroup)(e.target.value)}
                displayEmpty
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuPaperProps}
                sx={selectSx}
                renderValue={(v) => {
                  if (!v)
                    return (
                      <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                        Välj grupp
                      </Box>
                    );
                  return groupOptions.find((g) => g.id === v)?.label || "";
                }}
              >
                <MenuItem value="" disabled>
                  <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                    Välj grupp
                  </Box>
                </MenuItem>
                {groupOptions.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FieldLabel>Anläggning</FieldLabel>
            <FormControl fullWidth>
              <Select
                value={facility}
                onChange={(e) => markEdited(setFacility)(e.target.value)}
                displayEmpty
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuPaperProps}
                sx={selectSx}
                renderValue={(v) => {
                  if (!v)
                    return (
                      <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                        Ingen anläggning
                      </Box>
                    );
                  return facilityOptions.find((f) => f.id === v)?.label || "";
                }}
              >
                <MenuItem value="">
                  <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                    Ingen anläggning
                  </Box>
                </MenuItem>
                {facilityOptions.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Box
          component="button"
          type="button"
          onClick={() => {
            onClose && onClose();
            navigate(`/admin/user/movements/${userId}`);
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            background: "transparent",
            color: INK,
            border: `1px solid ${LINE}`,
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.25s ease",
            "&:hover": {
              background: "rgba(10,22,40,0.025)",
              borderColor: "rgba(10,22,40,0.22)",
            },
          }}
        >
          <TimelineRoundedIcon sx={{ fontSize: 16 }} />
          Visa rörelseanalys
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          pt: 3,
          borderTop: `1px solid ${LINE_SOFT}`,
          flexWrap: "wrap",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={handleDeleteClick}
          disabled={deleting}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            background: confirmDelete ? BRAND : "transparent",
            color: confirmDelete ? "#fff" : BRAND,
            border: `1px solid ${
              confirmDelete ? BRAND : "rgba(138,17,37,0.26)"
            }`,
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: deleting ? "wait" : "pointer",
            transition: "all 0.25s ease",
            opacity: deleting ? 0.6 : 1,
            "&:hover": {
              background: confirmDelete ? "#6b0d1d" : "rgba(138,17,37,0.06)",
              borderColor: BRAND,
            },
          }}
        >
          {deleting ? (
            <CircularProgress size={14} sx={{ color: BRAND }} />
          ) : (
            <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
          )}
          {confirmDelete ? "Klicka igen för att bekräfta" : "Ta bort användare"}
        </Box>

        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            sx={{
              px: 2.25,
              py: 1.25,
              background: "transparent",
              color: INK_MUTED,
              border: `1px solid ${LINE}`,
              borderRadius: "10px",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.25s ease",
              "&:hover": {
                color: INK,
                borderColor: "rgba(10,22,40,0.22)",
              },
            }}
          >
            Avbryt
          </Box>
          <Box
            component="button"
            type="button"
            onClick={handleUpdate}
            disabled={!canSave}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.75,
              py: 1.25,
              background: INK,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              cursor: canSave ? "pointer" : "not-allowed",
              opacity: canSave ? 1 : 0.4,
              transition: "all 0.25s ease",
              boxShadow: canSave
                ? "0 10px 24px -12px rgba(10,22,40,0.55)"
                : "none",
              "&:hover": canSave
                ? { background: "#1a2638", transform: "translateY(-1px)" }
                : {},
            }}
          >
            {saving ? (
              <CircularProgress size={14} sx={{ color: "#fff" }} />
            ) : (
              <CheckRoundedIcon sx={{ fontSize: 16 }} />
            )}
            Spara ändringar
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
